/** @format */

import { memo } from 'react'

import styles from 'dir/styles/courses.module.css'

export const _4 = memo(() => (
  <section className={styles.section}>
    <h2 className={styles.h2}>4 - Основы Git</h2>

    <section className={styles.subsection}>
      <h3 className={styles.h3}>4.1 - Клонирование</h3>
      <p className={styles.description}>
        <b>Клонирование (Clone)</b> — скачивание репозитория с удалённого сервера на локальный компьютер в определённый
        каталог для дальнейшей работы с этим каталогом как с репозиторием.
      </p>
      <p className={styles.description}>
        В процессе клонирования удаленного репозитория, на локальном компьютере появится папка (имя папки соответствует
        имени репозитория), в которой будут находиться все файлы репозитория, и происходит связывание локальной папки и
        удаленного репозитория. Так же в ней появляется скрытая папка <code className={styles.code}>.git</code>, которая
        и есть локальным репозиторием, это служебная папка Git.
      </p>
      <p className={styles.description}>
        В результате клонирования, разработчик может работать с файлами проекта: изменять их, удалять или создавать
        новые. Эти изменения - локальные. В конце определенного этапа работы разработчик должен сделать коммит, т.е.
        зафиксировать произведенные изменения. То, после каких этапов работы делать коммиты, разработчик выбирает сам
        (это может быть конец разработки определенного элемента или просто окончание рабочего дня).
      </p>
      <p className={styles.notificationWarn}>
        При клонировании не происходит копирования только рабочей копии. Git получает копию практически всех данных,
        которые есть на удаленном сервере. При выполнении операции клонирования с сервера забирается каждая версия
        каждого файла из истории проекта.
      </p>
    </section>

    <section className={styles.subsection}>
      <h3 className={styles.h3}>4.2 - Коммит</h3>
      <p className={styles.description}>
        <b>Коммит (Commit)</b> — создание нового снимка состояния файлов проекта, фиксация изменений и запись изменений
        в локальный репозиторий. Коммит является минимальной структурной составляющей разработки, все коммиты хранятся в
        системе контроля версий, и разработчик может вернуться (откатить проект) к любому коммиту за всё время
        разработки.
      </p>

      <p className={styles.notificationWarn}>
        Каждый раз, когда вы делаете коммит, то есть сохраняете состояние своего проекта в Git, система запоминает, как
        выглядит каждый файл в этот момент и сохраняет ссылку на этот снимок. Для увеличения эффективности, если файлы
        не были изменены, Git не запоминает эти файлы вновь, а только создаёт ссылку на предыдущую версию идентичного
        файла, который уже сохранён.
      </p>
    </section>

    <section className={styles.subsection}>
      <h3 className={styles.h3}>4.3 - Пуш</h3>
      <p className={styles.description}>
        <b>Пуш (Push)</b> — отправка всех новых коммитов на удалённый репозиторий.
      </p>
      <p className={styles.description}>
        Когда вы хотите добавить свои локальные наработки на удаленный репозиторий, необходимо сделать пуш (push). Эта
        операция срабатывает только в случае совпадения 2-х условий:
      </p>

      <ul className={styles.list}>
        <li className={styles.list__item}>
          если вы клонировали репозиторий с сервера, на котором у вас есть права на запись
        </li>
        <li className={styles.list__item}>и если никто другой с тех пор не выполнял команду push</li>
      </ul>

      <p className={styles.notificationWarn}>
        Если после того как вы произвели клонирование кто-то с правом на запись выполнил команду push, то ваш push точно
        будет отклонён. Чтобы команда push сработала, потребуется получить изменения ( команда pull), а затем полученные
        изменения объединить с вашими (merge). Это релевантно только при командной разработке, если вы единственный
        участник процесса разработки, таких ситуаций не будет.
      </p>
    </section>

    <section className={styles.subsection}>
      <h3 className={styles.h3}>4.4 - Пул</h3>
      <p className={styles.description}>
        <b>Пул (Pull)</b> — получение последних изменений с удалённого репозитория.
      </p>
      <p className={styles.description}>
        Git автоматически извлекает и затем сливает данные из удалённой ветки в вашу текущую ветку. К тому же по
        умолчанию операция клонированая автоматически настраивает вашу локальную ветку master на отслеживание удалённой
        ветки master на сервере, с которого вы клонировали (подразумевается, что на удалённом сервере есть ветка
        master). Выполнение пула, как правило, извлекает данные с сервера, с которого вы изначально склонировали, и
        автоматически пытается слить (merge) их с кодом, над которым вы в данный момент работаете.
      </p>
    </section>

    <section className={styles.subsection}>
      <h3 className={styles.h3}>4.5 - Ветки</h3>
      <p className={styles.description}>
        <b>Ветка (Branch)</b> — это параллельная версия репозитория. Она включена в этот репозиторий, но не влияет на
        главную версию, тем самым позволяя свободно работать в параллельной. Когда вы внесли нужные изменения, то вы
        можете объединить их с главной версией.
      </p>
      <p className={styles.description}>
        Над проектом обычно работает не один человек. Разработка заключается в написании определенного модуля,
        исправлении некоторой ошибки, либо любой другой активности, которая требует автономности. Для этого и
        используются ветки. Имя ветки чаще всего выбирается по названию модуля или исправления.
      </p>

      <section className={styles.subsection}>
        <h4 className={styles.h4}>4.5.1 - Процесс использования ветки</h4>
        <ul className={styles.list}>
          <li className={styles.list__item}>
            В начале работы над исправлениями или новыми модулями разработчик создает новую ветку из главной.
          </li>
          <li className={styles.list__item}>
            В процессе работы по необходимости (при завершении определенной части работы), разработчик делает коммиты в
            созданную ветку.
          </li>
          <li className={styles.list__item}>
            После окончания работы с веткой (полностью разработан новый модуль или исправлена ошибка), разработчик
            делает слияние созданной ветки с главной.
          </li>
          <li className={styles.list__item}>
            Если в дальнейшем работать с веткой не планируется, она удаляется. Если во время работы с новой веткой никто
            из разработчиков, работающих с данным проектом, на изменял файлы, которые были изменены в процессе работы с
            новой веткой, то слияние происходит. В противном случае возникают конфликты, которые необходимо разрешить (о
            конфликтах позже).
          </li>
        </ul>
        <p className={styles.notificationWarn}>
          В процессе разработки есть возможность переключаться между ветками. При этом в локальной папке, связанной с
          репозиторием, будет отображаться та версия файлов, которая соответствует текущей ветке
        </p>
      </section>

      <section className={styles.subsection}>
        <h4 className={styles.h4}>4.5.2 - Мастер</h4>
        <p className={styles.description}>
          <b>Мастер (Master)</b> — главная или основная ветка репозитория. Создается по умолчанию в момент создания
          репозитория.
        </p>
      </section>

      <section className={styles.subsection}>
        <h4 className={styles.h4}>4.5.3 - Мёрдж</h4>
        <p className={styles.description}>
          <b>Мёрдж (Merge)</b> — слияние изменений из какой-либо ветки репозитория с любой веткой этого же репозитория.
          Чаще всего это слияние ветки с изменениями с основной веткой репозитория.
        </p>
      </section>
    </section>

    <section className={styles.subsection}>
      <h3 className={styles.h3}>4.6 - Конфликт</h3>
      <p className={styles.description}>
        В некоторых случаях, при слиянии текущей ветки и главной (master), могут возникать конфликты. Причина их
        возникновения всегда одна - определенные файлы, с которыми велась работа в созданной ветке, параллельно были
        изменены и в мастере. И теперь, при слиянии, нет однозначного варианта сшития контента файлов, который бы не
        повлек потерю данных. В этом случае разработчику необходимо определить, какие изменения должны быть оставлены, а
        какие отброшены.
      </p>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>4.6.1 - Разрешение конфликтов</h4>
        <p className={styles.description}>
          Разрешение конфликта заключается в том, что на основе двух версий файлов (той, которая уже лежит в мастере и
          той, которую вы собираетесь туда положить) нужно сформировать итоговый файл.
        </p>
      </section>
    </section>

    <section className={styles.subsection}>
      <h3 className={styles.h3}>4.7 - Форк</h3>
      <p className={styles.description}>
        <b>Форк (Fork)</b> — копия репозитория. Его также можно рассматривать как внешнюю ветку для текущего
        репозитория. Копия вашего открытого репозитория на Гитхабе может быть сделана любым пользователем, после чего он
        может прислать изменения в ваш репозиторий через пулреквест.
      </p>
    </section>

    <section className={styles.subsection}>
      <h3 className={styles.h3}>4.8 - Пулреквест</h3>
      <p className={styles.description}>
        <b>Пулреквест (Pull Request)</b> — запрос на слияние форка репозитория с основным репозиторием. Пулреквест может
        быть принят или отклонён вами, как владельцем репозитория.
      </p>
    </section>
  </section>
))
